import { Marker } from 'maplibre-gl';
import { C } from '../map/tokens.js';
import { toast } from './toast.js';
import { umbenennen } from './umbenennen.js';

function markerElement() {
  const el = document.createElement('div');
  el.style.cssText = `width:14px;height:14px;border-radius:50%;background:${C.favorite};
    border:2.5px solid #fff;box-shadow:0 1px 3px rgba(0,0,0,.35);cursor:pointer`;
  return el;
}

/** Gespeicherte Reise statt Ort? Dann fuehrt der Eintrag in die Routenplanung. */
const istReise = (f) => Boolean(f.reise);

const MITTEL = { auto: 'Auto', bicycle: 'Velo', pedestrian: 'Zu Fuss', transit: 'ÖV' };

/** „ÖV · ab Do 09.10., 08:30“ - oder nur das Verkehrsmittel, wenn keine Zeit dabei ist. */
function reiseMeta(reise) {
  const mittel = MITTEL[reise.mode] || 'Route';
  if (!reise.zeit) return mittel;
  const d = new Date(reise.zeit);
  if (Number.isNaN(d.getTime())) return mittel;
  const wann = d.toLocaleString('de-CH', {
    weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  });
  return `${mittel} · ${reise.ankunft ? 'an' : 'ab'} ${wann}`;
}

/** Rund zehn Meter - genug für ein zweites Antippen desselben Lokals. */
const naheBei = (a, b) => Math.abs(a.lat - b.lat) < 1e-4 && Math.abs(a.lng - b.lng) < 1e-4;
/** Rund zwei Meter - für Stellen ohne Namen, an denen sonst nichts unterscheidet. */
const genauDa = (a, b) => Math.abs(a.lat - b.lat) < 2e-5 && Math.abs(a.lng - b.lng) < 2e-5;

export function createFavoritesPanel({ map, host, badge, modeEl, backend, getProfile, onNavigate, onReise, onZeigen }) {
  let items = [];
  const markers = new Map();

  function syncMarkers() {
    for (const [id, m] of markers) {
      if (!items.some((f) => f.id === id && !istReise(f))) { m.remove(); markers.delete(id); }
    }
    for (const f of items) {
      // Eine Reise ist kein Ort - sie bekommt keine Marke in der Karte.
      if (istReise(f) || markers.has(f.id)) continue;
      const m = new Marker({ element: markerElement(), anchor: 'center' })
        .setLngLat([f.lng, f.lat])
        .addTo(map);
      m.getElement().title = f.name;
      // Nicht weiter zur Karte: die machte sonst aus demselben Klick eine zweite
      // Auskunft über das, was zufällig unter der Marke liegt.
      // Nach der Kennung nachschlagen: der Favorit kann inzwischen anders heissen.
      m.getElement().addEventListener('click', (e) => {
        e.stopPropagation();
        flyTo(items.find((x) => x.id === f.id) || f);
      });
      markers.set(f.id, m);
    }
  }

  function flyTo(f) {
    map.flyTo({ center: [f.lng, f.lat], zoom: f.zoom || 16, duration: 700 });
    onZeigen?.(f);
    onNavigate?.();
  }

  function render() {
    host.textContent = '';
    badge.textContent = String(items.length);
    badge.hidden = items.length === 0;

    const profile = getProfile();
    if (modeEl) {
      modeEl.textContent = profile
        ? profile.local ? `lokal: ${profile.name}` : profile.name
        : 'nicht angemeldet';
    }

    if (!items.length) {
      const p = document.createElement('div');
      p.className = 'empty';
      p.textContent = 'Noch keine Favoriten gespeichert.';
      host.append(p);
      syncMarkers();
      return;
    }

    for (const f of items) {
      const row = document.createElement('div');
      row.className = 'fav';

      const main = document.createElement('button');
      main.type = 'button';
      main.className = 'fav__main';
      const name = document.createElement('span');
      name.className = 'fav__name';
      name.textContent = f.name;
      const meta = document.createElement('span');
      meta.className = 'fav__meta';
      meta.textContent = istReise(f)
        ? reiseMeta(f.reise)
        : [f.kind, `${f.lat.toFixed(4)}, ${f.lng.toFixed(4)}`].filter(Boolean).join(' · ');
      main.append(name, meta);
      if (istReise(f)) {
        main.classList.add('fav__main--reise');
        main.addEventListener('click', () => { onReise?.(f.reise); onNavigate?.(); });
      } else {
        main.addEventListener('click', () => flyTo(f));
      }

      const del = document.createElement('button');
      del.type = 'button';
      del.className = 'fav__del';
      del.title = istReise(f) ? 'Reise löschen' : 'Favorit löschen';
      del.setAttribute('aria-label', `${istReise(f) ? 'Reise' : 'Favorit'} ${f.name} löschen`);
      del.textContent = '×';
      del.addEventListener('click', () => remove(f));

      const edit = document.createElement('button');
      edit.type = 'button';
      edit.className = 'fav__del';
      edit.title = 'Umbenennen';
      edit.setAttribute('aria-label', `${f.name} umbenennen`);
      edit.textContent = '✎';
      edit.addEventListener('click', () => {
        umbenennen(main, {
          wert: f.name,
          beschriftung: 'Neuer Name',
          onFertig: (neu) => (neu === f.name ? render() : rename(f, neu))
        });
      });

      row.append(main, edit, del);
      host.append(row);
    }
    syncMarkers();
  }

  async function load() {
    try {
      items = (await backend.favorites.list(getProfile())) || [];
    } catch {
      items = [];
      toast('Favoriten konnten nicht geladen werden.');
    }
    render();
  }

  async function add(fav) {
    try {
      const entry = await backend.favorites.add(getProfile(), fav);
      items = [entry, ...items];
      render();
      toast(istReise(entry) ? `Reise „${fav.name}“ gespeichert.` : `„${fav.name}“ gespeichert.`);
    } catch (err) {
      toast(err.message || 'Speichern fehlgeschlagen.');
    }
  }

  async function rename(fav, name) {
    try {
      await backend.favorites.update(getProfile(), fav.id, { name });
      items = items.map((f) => (f.id === fav.id ? { ...f, name } : f));
      // Die Marke trägt den Namen im Titel und wird nur beim Anlegen beschriftet.
      markers.get(fav.id)?.getElement().setAttribute('title', name);
    } catch {
      toast('Umbenennen fehlgeschlagen.');
    }
    render();
  }

  /** Löschen ohne Rückfrage, aber mit einem Weg zurück. */
  async function remove(fav) {
    try {
      await backend.favorites.remove(getProfile(), fav.id);
      items = items.filter((f) => f.id !== fav.id);
      render();
      toast(`„${fav.name}“ gelöscht.`, {
        aktion: 'Rückgängig',
        beiAktion: async () => {
          const zurueck = await backend.favorites.add(getProfile(), fav);
          items = [zurueck, ...items];
          render();
        }
      });
    } catch {
      toast('Löschen fehlgeschlagen.');
    }
  }

  /**
   * Liegt hier schon ein Favorit? Reisen zählen nicht - sie sind kein Ort.
   *
   * Die Nähe allein genügt nicht: in der Altstadt stehen zwei Lokale keine zehn Meter
   * auseinander. Hiess es beim Nachbarn „Gespeichert“, löschte ein Tippen den falschen.
   * Mit Namen zählt deshalb auch der Name, ohne Namen nur fast dieselbe Stelle.
   */
  const finde = (punkt) => items.find((f) => {
    if (istReise(f)) return false;
    return punkt.name ? f.name === punkt.name && naheBei(f, punkt) : genauDa(f, punkt);
  });

  return {
    load, add, remove, render,
    istFavorit: (punkt) => Boolean(finde(punkt)),
    umschalten: async (punkt, fav) => {
      const da = finde(punkt);
      if (da) await remove(da);
      else await add(fav);
    },
    get items() { return items; }
  };
}
